import type { LucideIcon } from "lucide-react";
import { Building2, GitBranch, Network, Store, Truck } from "lucide-react";

import type { ChainViewOrgType } from "@/api/chain/type";
import type { OrganizationOrgType } from "@/api/system/organizations/type";

/** 运营端系统标识，须与 JWT 登录端一致 */
export const OPS_SYSTEM_POINT = "ops";

/** 组织类型元数据（展示、短标签、图标与配色） */
export interface OrganizationOrgTypeMeta {
  label: string;
  shortLabel: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  borderColor: string;
}

/** 后端组织类型枚举（与 OrganizationOrgType 一致；含一级组织 CHAIN） */
export const ORGANIZATION_ORG_TYPES = [
  "PLATFORM",
  "AGENT",
  "SUPPLIER",
  "STORE",
  "CHAIN",
] as const satisfies readonly OrganizationOrgType[];

/** 组织类型统一配置（展示、短标签、可用端） */
export const ORGANIZATION_ORG_TYPE_META: Record<OrganizationOrgType, OrganizationOrgTypeMeta> = {
  PLATFORM: {
    label: "平台总部",
    shortLabel: "平台",
    icon: Building2,
    color: "text-amber-700",
    bgColor: "bg-amber-100",
    borderColor: "border-amber-200",
  },
  AGENT: {
    label: "代理",
    shortLabel: "代理",
    icon: Network,
    color: "text-blue-700",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
  },
  SUPPLIER: {
    label: "供应商",
    shortLabel: "供应商",
    icon: Truck,
    color: "text-orange-700",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200",
  },
  STORE: {
    label: "线下门店",
    shortLabel: "门店",
    icon: Store,
    color: "text-emerald-700",
    bgColor: "bg-emerald-50",
    borderColor: "border-emerald-200",
  },
  CHAIN: {
    label: "连锁",
    shortLabel: "连锁",
    icon: GitBranch,
    color: "text-violet-700",
    bgColor: "bg-violet-50",
    borderColor: "border-violet-200",
  },
};

/** 连锁视图节点类型（PLATFORM / CHAIN / STORE） */
export const CHAIN_VIEW_ORG_TYPES = [
  "PLATFORM",
  "CHAIN",
  "STORE",
] as const satisfies readonly ChainViewOrgType[];

/** 连锁视图组织类型配置（PLATFORM / CHAIN / STORE 复用组织类型元数据） */
export const CHAIN_VIEW_ORG_TYPE_META: Record<ChainViewOrgType, OrganizationOrgTypeMeta> = {
  PLATFORM: ORGANIZATION_ORG_TYPE_META.PLATFORM,
  CHAIN: ORGANIZATION_ORG_TYPE_META.CHAIN,
  STORE: ORGANIZATION_ORG_TYPE_META.STORE,
};

/** 端类型缩写（与后端 systemTypeCode 一致） */
export type AppType = "OPS" | "BIZ" | "POS" | "INT" | "KSK" | "PCT" | "WKS" | "MEM";

/** 端应用元数据：简称、缩写、终端展示名、背景色 */
export interface TerminalAppMeta {
  name: string;
  abbr: string;
  label: string;
  bgColor: string;
}

/** 各端应用统一配置 */
export const APP_INFO: Record<AppType, TerminalAppMeta> = {
  OPS: {
    name: "运营中心",
    abbr: "OPS",
    label: "炽竞—运营中心",
    bgColor: "bg-slate-700",
  },
  BIZ: {
    name: "经营中心",
    abbr: "BIZ",
    label: "炽竞—经营中心",
    bgColor: "bg-purple-500",
  },
  POS: {
    name: "收银中心",
    abbr: "POS",
    label: "炽竞—收银中心",
    bgColor: "bg-cyan-500",
  },
  INT: {
    name: "互动终端",
    abbr: "INT",
    label: "炽竞—互动终端",
    bgColor: "bg-blue-400",
  },
  KSK: {
    name: "自助终端",
    abbr: "KSK",
    label: "炽竞—自助终端",
    bgColor: "bg-indigo-500",
  },
  PCT: {
    name: "电脑终端",
    abbr: "PCT",
    label: "炽竞—电脑终端",
    bgColor: "bg-cyan-500",
  },
  WKS: {
    name: "炽竞工作台",
    abbr: "WKS",
    label: "炽竞工作台",
    bgColor: "bg-orange-500",
  },
  MEM: {
    name: "炽竞会员",
    abbr: "MEM",
    label: "炽竞会员",
    bgColor: "bg-emerald-500",
  },
};

/** 按终端缩写（如 OPS、WKS）取完整展示名 */
export function getTerminalLabel(abbr: string | undefined): string | undefined {
  return abbr ? APP_INFO[abbr as AppType]?.label : undefined;
}
